import { iconForGroupedFilter } from 'module/helpers/filter/entity-for-filter-type';
import {
  asFilterableFieldOption,
  asGroupedFilterableFieldOption,
  getFilterGroupType,
} from 'module/helpers/filter/grouped-field-transformer';
import { useTranslation } from 'module/hooks/use-translation';
import { FilterableField } from 'module/types/filter-field/field/filterable-field';
import { GroupedOptions } from 'module/types/filter-field/field/grouped-field';
import { FontAwesomeIcon } from 'module/ui/components/atoms/icon/icon';
import { InputStyle } from 'module/ui/components/atoms/input/input';
import { faChevronDown } from '@fortawesome/pro-regular-svg-icons';
import {
  PopoverSelect,
  type PopoverSelectGroup,
  type PopoverSelectOption,
} from '@rocketlane/ui';
import { flatten } from 'lodash';
import { useMemo } from 'react';
import styled from 'styled-components';

const TextWithIconWrapper = styled.div`
  display: inline-flex;
  align-items: center;
  max-width: 190px;
  min-width: 0;
`;

const DropdownIndicator = styled(FontAwesomeIcon)`
  color: var(--icon-secondary);
`;

const FieldSelectTrigger = styled.button`
  ${InputStyle};
  box-sizing: border-box;
  width: 240px;
  display: inline-flex;
  align-items: center;
  justify-content: space-between;
  color: var(--text-primary);
  cursor: pointer;
`;

const FieldSelectIcon = styled.span`
  display: flex;
  flex-shrink: 0;
  margin-right: var(--spacing-03);
  color: var(--icon-secondary);

  svg {
    margin: 0;
    fill: var(--icon-secondary);
  }
`;

const GroupIcon = styled.span`
  display: flex;
  color: var(--icon-secondary);

  svg {
    margin: 0;
    fill: var(--icon-secondary);
  }
`;

const TextWithEllipsis = styled.span`
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
  max-width: 190px;
`;

const DropdownTitle = styled.span`
  text-transform: capitalize;
`;

interface GroupedFieldSelectProps {
  fields: GroupedOptions<FilterableField>[];
  onChange: (field: FilterableField) => void;
  title: React.ReactNode;
  value?: FilterableField;
}

const hasFieldId = (
  option: PopoverSelectOption,
): option is PopoverSelectOption & { fieldId: number } =>
  'fieldId' in option && typeof option.fieldId === 'number';

export const GroupedFieldSelect = ({
  fields,
  onChange,
  title,
  value,
}: GroupedFieldSelectProps) => {
  const defaultFallback = useTranslation('select_with_ellipsis');
  const searchPlaceholder = useTranslation('search');
  const fieldAsOptions = useMemo(
    () => asGroupedFilterableFieldOption(fields),
    [fields],
  );
  const allFields = useMemo(
    () => flatten(fields.map((group) => group.options)),
    [fields],
  );
  const groups = useMemo<PopoverSelectGroup[]>(
    () =>
      fieldAsOptions.map((group) => ({
        key: group.id,
        label: group.label ?? '',
        items: group.options,
      })),
    [fieldAsOptions],
  );
  const groupIcons = useMemo(
    () => new Map(fields.map((group) => [group.id, group.icon])),
    [fields],
  );
  const selectedOption = useMemo(
    () => (value == null ? null : asFilterableFieldOption(value)),
    [value],
  );
  const selectedGroupType =
    value == null ? undefined : getFilterGroupType(value.sourceType);

  return (
    <div data-cy="grouped_criteria.field_selector">
      <PopoverSelect
        collapsible
        dropdownHeader={<DropdownTitle>{title}</DropdownTitle>}
        dropdownWidth={296}
        getOptionDataCy={(option) => `select-option-${option.label}`}
        groups={groups}
        maxListHeight={379}
        onChange={(option) => {
          if (option == null || Array.isArray(option) || !hasFieldId(option)) {
            return;
          }

          const selectedField = allFields.find(
            (field) => field.fieldId === option.fieldId,
          );

          if (selectedField != null) {
            onChange(selectedField);
          }
        }}
        renderGroupIcon={(group) => {
          const icon = groupIcons.get(group.key);

          return icon == null ? null : <GroupIcon>{icon}</GroupIcon>;
        }}
        renderTrigger={() => (
          <FieldSelectTrigger data-cy="select.fields" type="button">
            <TextWithIconWrapper>
              {selectedGroupType == null ? null : (
                <FieldSelectIcon>
                  {iconForGroupedFilter[selectedGroupType]}
                </FieldSelectIcon>
              )}
              <TextWithEllipsis>
                {selectedOption?.label ?? defaultFallback}
              </TextWithEllipsis>
            </TextWithIconWrapper>
            <DropdownIndicator icon={faChevronDown} />
          </FieldSelectTrigger>
        )}
        searchPlaceholder={searchPlaceholder}
        searchable
        showFooter={false}
        value={selectedOption}
      />
    </div>
  );
};
